import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BikeType,
  RidingStyle,
  RidingExperience,
  Flexibility,
  PainPoint,
  BIKE_OPTIONS,
  RIDING_STYLE_LABELS,
  RIDING_EXPERIENCE_LABELS,
  FLEXIBILITY_LABELS,
  PAIN_POINT_LABELS,
} from '../constants/bikeConfig';
import './VideoUpload.css';

interface VideoUploadProps {
  onVideosUploaded: (sideVideo: File | null, frontVideo: File | null) => void;
  onBack: () => void;
  bikeType?: BikeType;
  ridingStyle?: RidingStyle;
  experience?: RidingExperience;
  flexibility?: Flexibility;
  painPoints?: PainPoint[];
}

const VideoUpload: React.FC<VideoUploadProps> = ({
  onVideosUploaded,
  onBack,
  bikeType,
  ridingStyle,
  experience,
  flexibility,
  painPoints,
}) => {
  const navigate = useNavigate();
  const [sideVideo, setSideVideo]           = useState<File | null>(null);
  const [showGuide, setShowGuide]           = useState(false);

  const handleSideVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setSideVideo(e.target.files[0]);
  };

  const handleSubmit = () => {
    if (!sideVideo) {
      alert('Please upload a side view video');
      return;
    }
    onVideosUploaded(sideVideo, null);
  };

  const canSubmit = sideVideo !== null;

  return (
    <div className="video-upload-container">
      {showGuide && (
        <div className="setup-guide">
          <h2>Video Setup Guide</h2>

          <div className="guide-section">
            <h3>📹 What This Tool Does</h3>
            <ul>
              <li>Extracts 40 evenly-spaced frames from each video</li>
              <li>Detects your body position using pose estimation</li>
              <li>Analyses key angles and compares to your rider profile</li>
              <li>Works with any video format supported by your browser</li>
            </ul>
          </div>

          <div className="guide-section">
            <h3>🎥 Video Tips</h3>
            <ul>
              <li>Film from a true side view — camera at hub height</li>
              <li>Ensure your full body and both wheels are visible</li>
              <li>Higher resolution = better quality frames</li>
              <li>Upload side view and/or front view videos</li>
            </ul>
          </div>

          <button className="btn-secondary" onClick={() => setShowGuide(false)}>
            Got it, let's upload videos
          </button>
        </div>
      )}

      {!showGuide && (
        <div className="upload-section">
          <h2>Upload Your Videos</h2>
          <p className="upload-subtitle">
            Upload your cycling videos for frame extraction and pose analysis.
          </p>

          {bikeType && (
            <div className="profile-summary">
              <div className="profile-summary-pills">
                <span className="profile-pill">
                  <span className="profile-pill-label">Bike</span>
                  {BIKE_OPTIONS[bikeType].label}
                </span>
                {ridingStyle && (
                  <span className="profile-pill">
                    <span className="profile-pill-label">Position</span>
                    {RIDING_STYLE_LABELS[ridingStyle]}
                  </span>
                )}
                {experience && (
                  <span className="profile-pill">
                    <span className="profile-pill-label">Experience</span>
                    {RIDING_EXPERIENCE_LABELS[experience]}
                  </span>
                )}
                {flexibility && (
                  <span className="profile-pill">
                    <span className="profile-pill-label">Flexibility</span>
                    {FLEXIBILITY_LABELS[flexibility]}
                  </span>
                )}
                {painPoints && painPoints.length > 0 && (
                  <span className="profile-pill">
                    <span className="profile-pill-label">Issues</span>
                    {painPoints[0] === 'none'
                      ? 'None'
                      : painPoints
                          .map(p => PAIN_POINT_LABELS[p] ?? p)
                          .join(', ')}
                  </span>
                )}
              </div>
              <button
                className="profile-summary-edit"
                onClick={() => navigate('/onboarding')}
              >
                Edit profile
              </button>
            </div>
          )}

          <div className="upload-grid">
            <div className="upload-box">
              <h3>Side View</h3>
              <div className="upload-area">
                <input
                  type="file"
                  id="side-video"
                  accept="video/*"
                  onChange={handleSideVideoChange}
                  className="file-input"
                />
                <label htmlFor="side-video" className="file-label">
                  {sideVideo ? (
                    <div className="file-info">
                      <span className="file-icon">✓</span>
                      <span className="file-name">{sideVideo.name}</span>
                      <span className="file-size">
                        {(sideVideo.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  ) : (
                    <div className="file-placeholder">
                      <span className="upload-icon">📁</span>
                      <span>Click to upload side view video</span>
                      <span className="upload-hint">or drag and drop</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

          </div>

          <div className="upload-actions">
            <button className="btn-secondary" onClick={onBack}>
              Back
            </button>
            <button className="btn-secondary" onClick={() => setShowGuide(true)}>
              Video Tips
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Analyse My Fit
            </button>
          </div>

          <div className="upload-note">
            <p>
              <strong>Note:</strong> At least one video is required. Side view is recommended for the most accurate analysis.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
